/**
 * System Settings Page
 * MCP servers, Mem0 config, First Message config, Agent Export/Import, Audit logs
 */

import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card } from '@/components/ui/Card';
import {
  MCPServersPanel,
  Mem0ConfigPanel,
  FirstMessagePanel,
  AgentConfigsExportImport,
  AuditLogViewer,
  ArtifactTypesPanel,
  ToolEnhancementPanel,
  PromptTemplatesPanel,
  OutputSchemasPanel,
  ToolComboPanel,
} from '@/features/system-settings';
import { Server, Brain, MessageSquare, History, ArrowUpDown, Package, Wrench, FileText, FileJson, Layers } from 'lucide-react';
import { useI18n } from '@/i18n';

export function SystemSettingsPage() {
  const { messages } = useI18n();
  const t = messages.systemSettings;
  const [activeTab, setActiveTab] = useState('mcp');
  const [auditType, setAuditType] = useState<'mcp' | 'mem0' | 'first-message' | 'artifact-types' | 'tool-enhancement' | 'tool-combo' | 'prompt-templates' | 'output-schemas'>('mcp');

  // Update audit type when switching tabs
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'mcp' || value === 'mem0' || value === 'first-message' || value === 'artifact-types' || value === 'tool-enhancement' || value === 'tool-combo' || value === 'prompt-templates' || value === 'output-schemas') {
      setAuditType(value);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
          <p className="text-gray-600 mt-1">
            {t.pageDescription}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="mcp" className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              {t.tabs.mcpServers}
            </TabsTrigger>
            <TabsTrigger value="mem0" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              {t.tabs.mem0Config}
            </TabsTrigger>
            <TabsTrigger value="first-message" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {t.tabs.firstMessage}
            </TabsTrigger>
            <TabsTrigger value="artifact-types" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              {t.tabs.artifactTypes}
            </TabsTrigger>
            <TabsTrigger value="tool-enhancement" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              {t.tabs?.toolEnhancement || 'Tool Enhancement'}
            </TabsTrigger>
            <TabsTrigger value="tool-combo" className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              {t.tabs?.toolCombo || 'Tool Combos'}
            </TabsTrigger>
            <TabsTrigger value="prompt-templates" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t.tabs?.promptTemplates || 'Prompt Templates'}
            </TabsTrigger>
            <TabsTrigger value="output-schemas" className="flex items-center gap-2">
              <FileJson className="w-4 h-4" />
              {t.tabs?.outputSchemas || 'Output Schemas'}
            </TabsTrigger>
            <TabsTrigger value="export-import" className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" />
              {t.tabs.exportImport}
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              {t.tabs.auditLogs}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mcp">
            <MCPServersPanel />
          </TabsContent>

          <TabsContent value="mem0">
            <Mem0ConfigPanel />
          </TabsContent>

          <TabsContent value="first-message">
            <FirstMessagePanel />
          </TabsContent>

          <TabsContent value="artifact-types">
            <ArtifactTypesPanel />
          </TabsContent>

          <TabsContent value="tool-enhancement">
            <ToolEnhancementPanel />
          </TabsContent>

          <TabsContent value="tool-combo">
            <ToolComboPanel />
          </TabsContent>

          <TabsContent value="prompt-templates">
            <PromptTemplatesPanel />
          </TabsContent>

          <TabsContent value="output-schemas">
            <OutputSchemasPanel />
          </TabsContent>

          <TabsContent value="export-import">
            <AgentConfigsExportImport />
          </TabsContent>

          <TabsContent value="audit">
            <div className="space-y-6">
              {/* Audit Type Selector */}
              <Card className="p-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700">
                    {t.audit.viewLogsFor}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAuditType('mcp')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'mcp'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs.mcpServers}
                    </button>
                    <button
                      onClick={() => setAuditType('mem0')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'mem0'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs.mem0Config}
                    </button>
                    <button
                      onClick={() => setAuditType('first-message')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'first-message'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs.firstMessage}
                    </button>
                    <button
                      onClick={() => setAuditType('artifact-types')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'artifact-types'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs.artifactTypes}
                    </button>
                    <button
                      onClick={() => setAuditType('tool-enhancement')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'tool-enhancement'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs?.toolEnhancement || 'Tool Enhancement'}
                    </button>
                    <button
                      onClick={() => setAuditType('tool-combo')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'tool-combo'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs?.toolCombo || 'Tool Combos'}
                    </button>
                    <button
                      onClick={() => setAuditType('prompt-templates')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'prompt-templates'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs?.promptTemplates || 'Prompt Templates'}
                    </button>
                    <button
                      onClick={() => setAuditType('output-schemas')}
                      className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                        auditType === 'output-schemas'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.tabs?.outputSchemas || 'Output Schemas'}
                    </button>
                  </div>
                </div>
              </Card>

              {/* Audit Logs */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {auditType === 'mcp' && t.audit.mcpServersLog}
                  {auditType === 'mem0' && t.audit.mem0ConfigLog}
                  {auditType === 'first-message' && t.audit.firstMessageLog}
                  {auditType === 'artifact-types' && t.audit.artifactTypesLog}
                  {auditType === 'tool-enhancement' && (t.audit?.toolEnhancementLog || 'Tool Enhancement Logs')}
                  {auditType === 'tool-combo' && (t.audit?.toolComboLog || 'Tool Combo Logs')}
                  {auditType === 'prompt-templates' && (t.audit?.promptTemplatesLog || 'Prompt Templates Logs')}
                  {auditType === 'output-schemas' && (t.audit?.outputSchemasLog || 'Output Schemas Logs')}
                </h2>
                <AuditLogViewer type={auditType} />
              </div>
            </div>
          </TabsContent>
        </Tabs>
    </div>
  );
}
